import { useRef, useState } from 'react';
import { CheckCircle2, Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { api, ErrorApi } from '../api';
import Ayuda from '../ui/Ayuda';
import { guardarArchivo } from '../ui/descarga';

/* =====================================================================
   EXCEL DE UNA CATEGORÍA · CATÁLOGO VIVO F7 (dentro del detalle)
   =====================================================================
   Bajar la hoja modelo (las columnas de siempre más las de la plantilla,
   con la unidad en el encabezado), exportar lo que hay e importar: primero
   se REVISA, celda por celda, y después se aplica TODO O NADA.
   ===================================================================== */
const mensaje = (e) => (e instanceof ErrorApi ? e.message : 'No hay conexión con el servidor. Vuelve a intentarlo.');
const BOTON = 'min-h-11 inline-flex items-center justify-center gap-2 font-semibold rounded-xl px-3 text-sm';

export default function ExcelDeCategoria({ categoria, puedeImportar }) {
  const [archivo, setArchivo] = useState(null);
  const [revision, setRevision] = useState(null);
  const [ocupado, setOcupado] = useState(null);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null);
  const entrada = useRef(null);
  const base = `/catalogo-vivo/catalogo/categorias/${categoria.id}`;

  async function bajar(que, nombre) {
    setOcupado(que);
    setError(null);
    try {
      guardarArchivo(await api.bajar(`${base}/${que}.xlsx`), `${nombre}.xlsx`);
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  }

  async function subir(confirmar) {
    setOcupado(confirmar ? 'aplicar' : 'revisar');
    setError(null);
    setHecho(null);
    try {
      const r = await api.subir(`${base}/importar?confirmar=${confirmar}`, archivo);
      setRevision(r);
      if (r.aplicado) {
        setHecho(`Listo: ${r.nuevos} producto(s) nuevos y ${r.actualizados} actualizados en «${categoria.nombre}».`);
        setArchivo(null);
        setRevision(null);
        if (entrada.current) entrada.current.value = '';
      }
    } catch (e) {
      setError(mensaje(e));
    } finally {
      setOcupado(null);
    }
  }

  const listoParaAplicar = revision && !revision.aplicado && revision.total_errores === 0 && revision.filas > 0;
  return (
    <section className="rounded-lg bg-gray-50 p-3 space-y-2" data-catalogo-vivo="excel-categoria">
      <p className="text-sm font-semibold text-gray-800 inline-flex items-center gap-2">
        <FileSpreadsheet className="h-4 w-4" aria-hidden="true" /> Excel de esta categoría
      </p>
      <Ayuda id="catalogo_vivo.excel.hoja" />
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={ocupado !== null} onClick={() => bajar('hoja-modelo', `${categoria.nombre}-hoja-modelo`)}
          className={`${BOTON} bg-white border border-gray-300 text-gray-800 disabled:opacity-60`}>
          {ocupado === 'hoja-modelo' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />} Bajar hoja modelo
        </button>
        <button type="button" disabled={ocupado !== null || !puedeImportar} onClick={() => bajar('exportar', `${categoria.nombre}-productos`)}
          className={`${BOTON} bg-white border border-gray-300 text-gray-800 disabled:opacity-60`}>
          {ocupado === 'exportar' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className="h-4 w-4" aria-hidden="true" />} Exportar productos
        </button>
      </div>
      {puedeImportar ? (
        <div className="space-y-2">
          <label htmlFor={`cv-excel-${categoria.id}`} className="block text-sm text-gray-700">Importar una hoja llena</label>
          <input id={`cv-excel-${categoria.id}`} ref={entrada} type="file" accept=".xlsx"
            onChange={(e) => { setArchivo(e.target.files?.[0] || null); setRevision(null); setHecho(null); }}
            className="block w-full text-sm" />
          <Ayuda id="catalogo_vivo.excel.importar" />
          <button type="button" disabled={!archivo || ocupado !== null} onClick={() => subir(false)}
            className={`${BOTON} w-full bg-white border border-gray-300 text-gray-800 disabled:opacity-60`}>
            {ocupado === 'revisar' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Upload className="h-4 w-4" aria-hidden="true" />} Revisar la hoja
          </button>
          {!archivo && <p className="text-xs text-gray-600">Elige un archivo .xlsx para revisarlo.</p>}
        </div>
      ) : (
        <p className="text-xs text-gray-600" data-motivo="excel-sin-permiso">Exportar e importar lo hace quien puede dar de alta productos (permiso «Mantener el catálogo»).</p>
      )}
      {revision && !revision.aplicado && (
        <div className="space-y-2" data-catalogo-vivo="revision-excel">
          {revision.total_errores > 0 ? (
            <>
              <p className="text-sm text-red-700" role="alert">
                La hoja tiene {revision.total_errores} celda(s) por corregir. No se guardó nada. Corrígelas en el archivo y vuelve a revisarlo.
              </p>
              <ul className="space-y-1 max-h-64 overflow-y-auto">
                {revision.errores.map((e, i) => (
                  <li key={`${e.fila}-${e.columna}-${i}`} className="text-xs text-gray-800 rounded border border-red-100 p-2" data-error-excel={`${e.fila}-${e.columna}`}>
                    <span className="font-semibold">Fila {e.fila} · {e.columna}:</span> {e.mensaje}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-sm text-green-800">
              La hoja está bien: {revision.nuevos} producto(s) nuevos y {revision.actualizados} por actualizar.
            </p>
          )}
          <button type="button" disabled={!listoParaAplicar || ocupado !== null} onClick={() => subir(true)}
            className={`${BOTON} w-full bg-[var(--color-marca)] text-white disabled:opacity-60`}>
            {ocupado === 'aplicar' ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4" aria-hidden="true" />} Guardar todo
          </button>
          {!listoParaAplicar && <p className="text-xs text-gray-600" data-motivo="aplicar-excel">Se guarda cuando la revisión no tiene celdas por corregir.</p>}
        </div>
      )}
      {hecho && <p role="status" className="text-sm text-green-800 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4" aria-hidden="true" />{hecho}</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    </section>
  );
}
